
import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];

  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    /*
     * IMPORTANT:
     * Capture the server-disabled states only once.
     *
     * Previously, reconnecting this component could
     * mistake a minus button disabled at quantity 1
     * for a permanent server-disabled button.
     *
     * This can happen during page transitions or
     * DOM reconnection.
     */
    if (!this.initialized) {
      const { minusButton, plusButton } = this.refs;

      this.serverDisabledMinus = minusButton.disabled;
      this.serverDisabledPlus = plusButton.disabled;

      this.initialized = true;
    }

    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute(
      'data-cart-quantity',
      cartQty.toString()
    );

    this.updateCartQuantity();
  }

  /**
   * Checks whether the current quantity can be added to cart.
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();

    const quantityToAdd = value;

    const wouldExceedMax =
      max !== null &&
      cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets current quantity value.
   * @returns {string}
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets current quantity value.
   * @param {string} value
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints.
   *
   * @param {string} min
   * @param {string|null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;

    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);

    const effectiveMax = this.getEffectiveMax();

    let newValue = currentValue;

    if ((currentValue - newMin) % newStep !== 0) {
      newValue =
        newMin +
        Math.floor(
          (currentValue - newMin) / newStep
        ) * newStep;
    }

    newValue = Math.max(
      newMin,
      Math.min(
        effectiveMax ?? Infinity,
        newValue
      )
    );

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current quantity constraints and values.
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(
        quantityInput.min,
        1
      ),

      max: parseIntOrDefault(
        quantityInput.max,
        null
      ),

      step: parseIntOrDefault(
        quantityInput.step,
        1
      ),

      value: parseIntOrDefault(
        quantityInput.value,
        0
      ),

      cartQuantity: parseIntOrDefault(
        quantityInput.getAttribute('data-cart-quantity'),
        0
      ),
    };
  }

  /**
   * Effective maximum excludes quantity already in cart.
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } =
      this.getCurrentValues();

    if (max === null) {
      return null;
    }

    return Math.max(
      max - cartQuantity,
      min
    );
  }

  /**
   * Enables or disables controls based on quantity.
   *
   * Server-disabled controls remain disabled.
   * Other controls can change state normally.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;

    const { min, value } = this.getCurrentValues();

    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled =
        effectiveMax !== null &&
        value >= effectiveMax;
    }
  }

  /**
   * Changes quantity by the configured increment.
   *
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;

    const { min, step, value } =
      this.getCurrentValues();

    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(
        min,
        value + step * stepMultiplier
      )
    );

    quantityInput.value = newValue.toString();

    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Increase quantity.
   *
   * Works when the click target is an HTML element
   * or a nested SVG element.
   *
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(1);
  }

  /**
   * Decrease quantity.
   *
   * Works when the click target is an HTML element
   * or a nested SVG element.
   *
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(-1);
  }

  /**
   * Select all text when the quantity input is focused.
   *
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;

    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !== quantityInput
    ) {
      return;
    }

    quantityInput.select();
  }

  /**
   * Validate and apply a manually entered quantity.
   *
   * @param {Event} event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) {
      return;
    }

    event.preventDefault();

    const { quantityInput } = this.refs;

    const { min, step } = this.getCurrentValues();

    const effectiveMax = this.getEffectiveMax();

    const quantity = Math.min(
      effectiveMax ?? Infinity,
      Math.max(
        min,
        parseInt(event.target.value) || 0
      )
    );

    if ((quantity - min) % step !== 0) {
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();

    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Broadcast the quantity change to parent components.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;

    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        Number(quantityInput.dataset.cartLine) || undefined
      )
    );
  }

  /**
   * Update quantity state after cart changes.
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;

    const { min, value } = this.getCurrentValues();

    const effectiveMax = this.getEffectiveMax();

    const clampedValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, value)
    );

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Get quantity input.
   *
   * @returns {HTMLInputElement}
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent
  );
}
